/**
 * Generador de contraseñas para cuentas nuevas.
 *
 * Se arma con `crypto.getRandomValues`, no con `Math.random`: esta contraseña
 * es la llave de una cuenta y `Math.random` es predecible.
 *
 * Del alfabeto se sacan los caracteres que se confunden al dictar por teléfono
 * o al copiar de un papel —O/0, l/I/1— porque estas contraseñas se transmiten
 * a mano. Se pierde algo de entropía por carácter y se compensa con el largo:
 * 16 caracteres de este alfabeto son ~92 bits, de sobra para el caso.
 */
const MAYUSCULAS = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // sin I ni O
const MINUSCULAS = 'abcdefghijkmnopqrstuvwxyz'; // sin l
const DIGITOS = '23456789'; // sin 0 ni 1
const SIMBOLOS = '@#$%&*+=?';
const ALFABETO = MAYUSCULAS + MINUSCULAS + DIGITOS + SIMBOLOS;

export const LARGO_PASSWORD = 16;

/** Entero uniforme en [0, tope) sin el sesgo del módulo. */
function alAzar(tope: number): number {
  const limite = Math.floor(0xffffffff / tope) * tope;
  const buffer = new Uint32Array(1);
  for (;;) {
    crypto.getRandomValues(buffer);
    if (buffer[0]! < limite) return buffer[0]! % tope;
  }
}

const uno = (fuente: string) => fuente[alAzar(fuente.length)]!;

export function generarPassword(largo = LARGO_PASSWORD): string {
  // Se garantiza al menos uno de cada familia: si no, el azar puede entregar
  // una contraseña sin dígitos que después rechace la política de otro sistema.
  const obligatorios = [uno(MAYUSCULAS), uno(MINUSCULAS), uno(DIGITOS), uno(SIMBOLOS)];
  const resto = Array.from({ length: Math.max(0, largo - obligatorios.length) }, () => uno(ALFABETO));
  const caracteres = [...obligatorios, ...resto];

  // Barajado de Fisher-Yates: sin esto, los cuatro obligatorios quedarían
  // siempre en las mismas posiciones y el patrón sería adivinable.
  for (let i = caracteres.length - 1; i > 0; i--) {
    const j = alAzar(i + 1);
    [caracteres[i], caracteres[j]] = [caracteres[j]!, caracteres[i]!];
  }
  return caracteres.join('');
}
